import { api } from './client';

/**
 * Событие «на девайсе сняли скриншот внутри приложения».
 *
 * Поля с префиксом device_* и характеристики антифрода приезжают джойном с
 * device_reports по (device_id, bundle_id) — они отражают ТЕКУЩЕЕ состояние
 * девайса, а не снимок на момент скриншота. Если репорта от девайса ещё не
 * было, все они null.
 */
export interface DeviceScreenshot {
  id: string;
  device_id: string;
  bundle_id: string;
  app_id: string | null;
  source: string;
  screen: string | null;
  app_version: string | null;
  sdk_version: string | null;
  device_model: string | null;
  os_version: string | null;
  ip_country: string | null;
  is_test: number;
  detected_at: string | null;
  created_at: string;
  // Снимок состояния батареи в момент события; null у старых версий SDK.
  battery_level: number | null;
  is_charging: number | null;
  // Картинка приходит вторым вызовом SDK и может не прийти вовсе.
  has_image: number;
  image_content_type: string | null;
  image_size_bytes: number | null;
  image_uploaded_at: string | null;
  // Из device_reports.
  has_sim: number | null;
  sim_country: string | null;
  geo_match: number | null;
  device_clean: number | null;
  sdk_status: string | null;
  block_reason: string | null;
  motion_flag: string | null;
  last_ad_outcome: string | null;
  screenshot_count: number | null;
  device_first_seen: string | null;
  device_updated_at: string | null;
}

export interface DeviceScreenshotStats {
  total: number;
  with_image: number;
  unique_devices: number;
  last_24h: number;
  by_source: Record<string, number>;
}

export interface ScreenshotFacetValue {
  value: string;
  count: number;
}

export interface DeviceScreenshotFacets {
  bundles: ScreenshotFacetValue[];
  sources: ScreenshotFacetValue[];
}

export interface DeviceScreenshotListResponse {
  screenshots: DeviceScreenshot[];
  total: number;
  page: number;
  limit: number;
}

export interface DeviceScreenshotQuery {
  bundle?: string[];
  source?: string[];
  country?: string[];
  hasImage?: 'yes' | 'no' | 'all';
  test?: 'only' | 'exclude' | 'all';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

function buildFilterParams(params: DeviceScreenshotQuery): URLSearchParams {
  const query = new URLSearchParams();
  const setMulti = (key: string, vals?: string[]) => {
    if (vals && vals.length > 0) query.set(key, vals.join(','));
  };
  setMulti('bundle', params.bundle);
  setMulti('source', params.source);
  setMulti('country', params.country);
  if (params.hasImage && params.hasImage !== 'all') query.set('has_image', params.hasImage);
  if (params.test && params.test !== 'all') query.set('test', params.test);
  if (params.search) query.set('search', params.search);
  if (params.dateFrom) query.set('date_from', params.dateFrom);
  if (params.dateTo) query.set('date_to', params.dateTo);
  return query;
}

export async function getDeviceScreenshots(params: DeviceScreenshotQuery): Promise<DeviceScreenshotListResponse> {
  try {
    const query = buildFilterParams(params);
    if (params.sortBy) query.set('sort_by', params.sortBy);
    if (params.sortDir) query.set('sort_dir', params.sortDir);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    return await api.get<DeviceScreenshotListResponse>(`/admin/devices/screenshots?${query.toString()}`);
  } catch {
    return { screenshots: [], total: 0, page: 1, limit: 50 };
  }
}

export async function getDeviceScreenshotStats(params: DeviceScreenshotQuery = {}): Promise<DeviceScreenshotStats> {
  try {
    const qs = buildFilterParams(params).toString();
    return await api.get<DeviceScreenshotStats>(`/admin/devices/screenshots/stats${qs ? `?${qs}` : ''}`);
  } catch {
    return { total: 0, with_image: 0, unique_devices: 0, last_24h: 0, by_source: {} };
  }
}

export async function getDeviceScreenshotFacets(): Promise<DeviceScreenshotFacets> {
  try {
    return await api.get<DeviceScreenshotFacets>('/admin/devices/screenshots/facets');
  } catch {
    return { bundles: [], sources: [] };
  }
}

/** Картинка отдаётся только с авторизацией — тянем блоб, а не ставим src. */
export async function getDeviceScreenshotImage(id: string): Promise<Blob> {
  return api.blob(`/admin/devices/screenshots/${id}/image`);
}
